import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { costCenterEnableDisable } from '@/api/domains/organization/costCenters';
import { useUI } from '@/hooks/ui/useUI';


interface UseCostCenterEnableDisableResult {
  enableCostCenter: (costCenterId: number) => Promise<boolean>;
  disableCostCenter: (costCenterId: number) => Promise<boolean>;
  isEnabling: boolean;
  isDisabling: boolean;
  enablingIds: Set<number>;
  disablingIds: Set<number>;
}

export function useCostCenterEnableDisable(): UseCostCenterEnableDisableResult {
  const { t: translate } = useTranslation();
  const { showNotification } = useUI();
  const [isEnabling, setIsEnabling] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [enablingIds, setEnablingIds] = useState<Set<number>>(new Set());
  const [disablingIds, setDisablingIds] = useState<Set<number>>(new Set());

  const enableCostCenter = useCallback(async (costCenterId: number): Promise<boolean> => {
    setIsEnabling(true);
    setEnablingIds((previous) => new Set(previous).add(costCenterId));

    const response = await costCenterEnableDisable.enable(costCenterId);

    setEnablingIds((previous) => {
      const next = new Set(previous);
      next.delete(costCenterId);
      return next;
    });
    setIsEnabling(false);

    if (response.success) {
      showNotification('success', translate('organization:messages.costCenterEnabled'));
      return true;
    }

    showNotification(
      'error',
      response.error?.detail || translate('organization:messages.costCenterEnableError'),
    );
    return false;
  }, [showNotification, translate]);

  const disableCostCenter = useCallback(async (costCenterId: number): Promise<boolean> => {
    setIsDisabling(true);
    setDisablingIds((previous) => new Set(previous).add(costCenterId));

    const response = await costCenterEnableDisable.disable(costCenterId);

    setDisablingIds((previous) => {
      const next = new Set(previous);
      next.delete(costCenterId);
      return next;
    });
    setIsDisabling(false);

    if (response.success) {
      showNotification('success', translate('organization:messages.costCenterDisabled'));
      return true;
    }

    showNotification(
      'error',
      response.error?.detail || translate('organization:messages.costCenterDisableError'),
    );
    return false;
  }, [showNotification, translate]);

  return {
    enableCostCenter,
    disableCostCenter,
    isEnabling,
    isDisabling,
    enablingIds,
    disablingIds,
  };
}

export default useCostCenterEnableDisable;
